import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShoppingBag, ArrowLeft } from "lucide-react";
import { useApp } from "../context/AppContext";
import { fetchArtist, fetchProducts } from "../lib/api";
import { langField } from "../lib/i18n";
import { ProductCard } from "../components/ProductCard";

export default function ArtistStore() {
  const { slug } = useParams();
  const { t, lang } = useApp();
  const [artist, setArtist] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    fetchArtist(slug)
      .then(async (a) => {
        setArtist(a);
        const prods = await fetchProducts({ artist: a.name });
        setProducts(prods);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-20">
        <p className="font-mono text-xs text-kult-muted">LOADING...</p>
      </div>
    );
  }
  if (notFound || !artist) {
    return (
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-20 text-center" data-testid="artist-store-not-found">
        <p className="font-display text-5xl">NOT FOUND.</p>
        <Link to="/artists" className="inline-flex items-center gap-2 mt-6 font-mono text-xs tracking-[0.25em] text-kult-yellow kult-link">
          <ArrowLeft size={14} /> BACK TO ROSTER
        </Link>
      </div>
    );
  }

  const role = lang === "pt" ? artist.role_pt : artist.role;
  const bio = lang === "pt" ? artist.bio_pt : artist.bio;
  const quote = lang === "pt" ? artist.quote_pt : artist.quote;

  return (
    <div data-testid="artist-store-page">
      {/* Hero */}
      <section className="relative bg-kult-black2 border-b border-kult-border overflow-hidden">
        {artist.image && (
          <img
            src={artist.image}
            alt={artist.name}
            className="absolute inset-0 w-full h-full object-cover opacity-25 grayscale"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/60 to-black" />
        <div className="relative max-w-[1600px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <Link to={`/artists/${artist.slug}`} className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] text-kult-yellow kult-link mb-4" data-testid="artist-store-back">
            <ArrowLeft size={12} /> {artist.name}'S PAGE
          </Link>
          <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow">/ {role}</p>
          <h1 className="font-display text-6xl md:text-8xl lg:text-9xl leading-none mt-2" data-testid="artist-store-name">
            {artist.name}
          </h1>
          {quote && (
            <p className="font-display text-xl md:text-3xl mt-4 max-w-2xl text-kult-pink italic" data-testid="artist-store-quote">
              "{quote}"
            </p>
          )}
          {bio && (
            <p className="text-kult-muted mt-6 max-w-2xl text-base md:text-lg leading-relaxed">{bio}</p>
          )}
          <div className="mt-8 inline-flex items-center gap-2 bg-kult-yellow text-black px-5 py-2 font-mono text-xs tracking-[0.3em]" data-testid="artist-store-count">
            <ShoppingBag size={14} />
            {products.length} {products.length === 1 ? "PIECE" : "PIECES"}
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="max-w-[1600px] mx-auto px-5 md:px-10 py-12 md:py-20">
        {products.length === 0 ? (
          <div className="border border-kult-border p-12 text-center" data-testid="artist-store-empty">
            <p className="font-display text-3xl">DROPS COMING SOON.</p>
            <p className="text-kult-muted mt-3">
              {lang === "pt"
                ? "Ainda não há merch atribuído a este artista. Volta em breve."
                : "No merch tagged to this artist yet. Check back soon."}
            </p>
            <Link
              to="/shop"
              className="inline-block mt-6 font-mono text-xs tracking-[0.25em] text-kult-yellow kult-link"
            >
              SHOP ALL →
            </Link>
          </div>
        ) : (
          <>
            <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow mb-2">
              / {artist.name} {lang === "pt" ? "COLEÇÃO" : "COLLECTION"}
            </p>
            <h2 className="font-display text-4xl md:text-5xl mb-10">
              {lang === "pt" ? "AS PEÇAS" : "THE PIECES"}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6" data-testid="artist-store-grid">
              {products.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
